import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Form, Col, Row } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";

import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs/components/prism-core';
import "prismjs/components/prism-json";
import 'prismjs/components/prism-markup';  
import 'prismjs/themes/prism.css'; //Example style, you can use another

import ConditionalButton from "../../../layout/conditional-button";
import ConditionalDropdown from "../../../layout/conditional-dropdown";
import BaseModal from "../../../layout/modal";
import ValueSelect from "../../../layout/select";
import FileModal from "../../../layout/dropzone-modal";
import TestModal from "./test";
import ScrollToTopButton from "./scroll-to-top-button";
import CustomFieldsEditor from "./custom-fields-editor";
import NativeHeaderFooterEditor from "./native-header-footer-editor";

import { withNavigation } from "../../../utils/wrapper";
import { isGuid } from "../../../utils/string";
import { format as formatXml } from "../../../utils/xml";
import {
    applyCustomValues,
    normalizeCustomFields,
    validateCustomFields,
} from "../../../utils/template-custom-fields";
import {
    style as renderingStyle,
    fileName as renderingFileName,
    download as renderedDownload,
    generators as renderingGenerators,
} from "../../../utils/rendering";

import { send as pageInfo, loading } from "../../../actions/page";
import { pack as alert } from "../../../actions/alert";
import {
    get as getTemplate,
    save as saveTemplate,
    destroy,
    render,
    inline,
    components as getComponents,
    saveComponent,
    componentAssignments as getComponentAssignments,
    saveComponentAssignments,
    compileTemplate,
    templateCompilation,
} from "../../../actions/report.template";
import { get as getSetting } from "../../../actions/settings";
import { unzip, destroy as deleteFile, base64 as asBase64 } from "../../../actions/files";

const EDITOR_STYLE = {
    fontFamily: '"Fira Code", Menlo, Consolas, "Liberation Mono", monospace',
    fontSize: 14,
    lineHeight: 1.5,
    // Match Prism background so selection looks right
    background: 'transparent',
    whiteSpace: "pre",        //* keep \n, don’t soft-wrap */
    overflowX: "auto",        //* horizontal scroll when needed */
    overflowY: "auto",
    overflowWrap: "normal",   //* don’t break long words */
    wordBreak: "normal"
};

const CATEGORY_OPTIONS = [    
    { value: "Billing",  label: "Billing" },
    { value: "Check", label: "Check" },
    { value: "Lead", label: "Lead" },
    { value: "Matter", label: "Matter" },
    { value: "Profile", label: "Profile" }
];

const OUTPUT_OPTIONS = [
    { value: "PDF", label: "PDF" },
    { value: "RTF", label: "RTF" },
    { value: "XSL", label: "XSL" },
    { value: "DXP", label: "DXP" }
];

class CodeEditorField extends React.PureComponent {
    render() {
        const {
            value,
            onValueChange,
            highlight,
            preClassName,
            textareaClassName,
            className,
            style,
        } = this.props;

        return (
            <Editor
                value={value ?? ""}
                onValueChange={onValueChange}
                highlight={highlight}
                padding={10}
                preClassName={preClassName}
                textareaClassName={textareaClassName}
                tabSize={2}
                insertSpaces
                style={style}
                className={className}
            />
        );
    }
}

CodeEditorField.propTypes = {
    value: PropTypes.string,
    onValueChange: PropTypes.func.isRequired,
    highlight: PropTypes.func.isRequired,
    preClassName: PropTypes.string,
    textareaClassName: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object,
};

CodeEditorField.defaultProps = {
    value: "",
    preClassName: undefined,
    textareaClassName: undefined,
    className: undefined,
    style: undefined,
};

class ReportTemplateItem extends React.Component {
    
    constructor(props) {
        super(props);
        const existingUuid = props.params.uuid;

        this.state = {
            uuid: isGuid(existingUuid) ? existingUuid : uuidv4(),
            name: "[New Report Template]",
            modal: { test: { show: false }, delete: { show: false }, upload: { show: false } },
            tooltips: {
                test: "Test Template",
                pending: "This feature will be enabled after you save your changes"
            },
            errors: {},
            output: "RTF",
            category: CATEGORY_OPTIONS[0].value,
            criteria: "",
            customFields: [],
            customFieldErrors: {},
            template: "",
            flags: 0,
            components: [],
            componentAssignments: [],
            componentStatus: "FULL",
            componentsLoaded: false,
            compiling: false,
        };
        this.props.pageInfo({ title: "Report Template", breadcrumb: this.state?.name });
        this.changed = false;
        this.loading = false;
        this.componentsChanged = false;
    }

    get uuid() {
        return this.props.params.uuid;
    }

    get isNew() {
        return !isGuid(this.uuid);
    }

    loadData = async () => {
        this.props.loading(true);
        this.loading = true;
        
        if (isGuid(this.uuid)) {
            const [templates, componentResult, assignmentResult, compilationResult] = await Promise.all([
                this.props.getTemplate(this.uuid),
                Promise.resolve(this.props.getComponents()).catch(() => []),
                Promise.resolve(this.props.getComponentAssignments(this.uuid)).catch(() => []),
                Promise.resolve(this.props.templateCompilation(this.uuid)).catch(() => ({ status: "FULL" })),
            ]);
            const template = (templates && templates.length) ? templates[0] : {};

            this.props.pageInfo({ title: "Report Template", breadcrumb: template?.name });
            template.template = template.content;
            const configuration = template.configuration || {};
            template.criteria = configuration.criteria;
            template.customFields = normalizeCustomFields(configuration.customFields);

            delete template.content;
            delete template.configuration;
            
            this.setState({
                ...template,
                components: componentResult || [],
                componentAssignments: assignmentResult || [],
                componentStatus: compilationResult?.status || "FULL",
                componentsLoaded: true,
            });
        } else {
            this.props.pageInfo({ title: "Report Template", breadcrumb: "[New Report Template]" });
        }

        this.changed = false;
        this.componentsChanged = false;
        this.loading = false;
        this.props.loading(false);
    }
    
    componentDidMount = () => {
        setTimeout(async () => {
            await this.loadData();
        }, 30);
    }

    componentDidUpdate(prevProps) {
        const prevUuid = prevProps?.params?.uuid ?? null;
        const nextUuid = this.props?.params?.uuid ?? null;

        if (prevUuid !== nextUuid) {
            this.loadData();
        }
    }

    onSaveClick = async (e) => {
        try {
            this.props.loading(true);
            this.loading = true;
            const {
                name,
                category,
                caption,
                fileName,
                output,
                template: content,
                criteria,
                customFields,
                flags,
                hidden,
                uuid,
            } = this.state;

            const customFieldValidation = validateCustomFields(customFields);
            if (!customFieldValidation.valid) {
                this.setState({ customFieldErrors: customFieldValidation.errors });
                return;
            }

            const configuration = {
                criteria,
                customFields: customFieldValidation.fields,
            }
        
            const result = await this.props.saveTemplate({
                category,
                name,
                flags,
                hidden,
                configuration,
                caption,
                fileName,
                output,
                content,
                uuid,
            });
            if (!result?.uuid) throw new Error("The report template could not be saved.");

            if (this.state.componentsLoaded) {
                const assignments = await this.props.saveComponentAssignments(result.uuid, this.state.componentAssignments);
                if (assignments.length) {
                    await this.props.compileTemplate(result.uuid);
                    this.setState({ componentAssignments: assignments, componentStatus: "READY" });
                } else {
                    this.setState({ componentAssignments: [], componentStatus: "FULL" });
                }
            }
            this.changed = false;
            this.componentsChanged = false;
            this.props.navigate(`/dashboard/templates/report/${result.uuid}`);
        } catch (error) {
            const payload = error?.response?.data;
            this.props.alert([
                payload?.error?.message || payload?.message || error?.message || "Report template save failed."
            ], "danger");
        } finally {
            this.loading = false;
            this.props.loading(false);
        }
    };
    

    onTestClick = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                test: { show: true }
            }
        }));
    }

    sanitizeFilename = (name) =>
        String(name || "")
            .replace(/[<>:"/\\|?*\x00-\x1F]/g, "_")  // windows forbidden + control chars
            .replace(/\s+/g, " ")
            .trim()
            .slice(0, 180); // keep it reasonable

    responseBlobText = async (blob) => {
        try {
            if (blob instanceof Blob) return await blob.text();
        } catch { }

        return "";
    }

    onTestOkModal = async (e) => {
        try {
            const outputType = renderingStyle(this.state.output);

            if (!outputType) {
                throw new Error("Unsupported report output.");
            }

            const reportGenerators = renderingGenerators({
                render: this.props.render,
            });

            const generate = reportGenerators[this.state.output];

            if (generate) {
                const dataset = applyCustomValues(e.dataset, e.custom, this.state.customFields);
                const result = await generate({
                    json: dataset,
                    uuid: this.state.uuid,
                    output: this.state.output,
                });

                if (result instanceof Blob && result.type?.includes("application/json")) {
                    const text = await this.responseBlobText(result);
                    throw new Error(text || "Report test returned JSON instead of a file.");
                }

                let resolvedFileName;

                try {
                    resolvedFileName = await renderingFileName({
                        inline: this.props.inline,
                        sanitizeFilename: this.sanitizeFilename,
                        fileName: this.state.fileName,
                        json: dataset,
                        output: this.state.output,
                    });
                } catch {
                    resolvedFileName = outputType.fallback;
                }

                renderedDownload({
                    result,
                    fileName: resolvedFileName,
                    mime: outputType.mime,
                });

                this.setState((prev) => ({
                    modal: {
                        ...prev.modal,
                        test: { show: false },
                    },
                }));
            }
        } catch (err) {
            const text = await this.responseBlobText(err?.response?.data);
            this.props.alert([text || err?.message || "Report test failed."], "danger");
            throw err;
        }
    }

    onChange = (e) => {
        this.setState({
            [e.target.name]: e.target.value
        });
        this.changed = true;
    }

    onCustomFieldsChange = (customFields) => {
        const validation = validateCustomFields(customFields);
        this.setState({
            customFields,
            customFieldErrors: validation.errors
        });
        this.changed = true;
    }

    onComponentAssignmentsChange = (componentAssignments) => {
        this.componentsChanged = true;
        this.setState({ componentAssignments, componentStatus: componentAssignments.length ? "STALE" : "FULL" });
    }

    onSaveComponent = async (component) => {
        const item = await this.props.saveComponent(component);
        const [components, compilation] = await Promise.all([
            this.props.getComponents(),
            isGuid(this.uuid) ? this.props.templateCompilation(this.uuid) : Promise.resolve({ status: "FULL" }),
        ]);
        this.setState({
            components: components || [],
            componentStatus: compilation?.status || (this.state.componentAssignments.length ? "STALE" : "FULL"),
        });
        return item;
    }

    onCompileComponents = async () => {
        try {
            this.setState({ compiling: true });
            const assignments = await this.props.saveComponentAssignments(this.uuid, this.state.componentAssignments);
            const result = await this.props.compileTemplate(this.uuid);
            this.componentsChanged = false;
            this.setState({
                compiling: false,
                componentAssignments: assignments,
                componentStatus: result?.status || "READY",
            });
        } catch (error) {
            const payload = error?.response?.data;
            this.setState({ compiling: false, componentStatus: "STALE" });
            this.props.alert([
                payload?.error?.message || payload?.message || error?.message || "Report template compilation failed."
            ], "danger");
        }
    }

    onTemplateChange = (e) => {
        this.onChange({
            target: {
                name: "template",
                value: e
            }
        });
    }

    onFlagChange = (checked, bit) => {
        this.setState(prev => {
            const flags = prev.flags ?? 0;
            const nextFlags = checked ? (flags | bit) : (flags & ~bit);
            return { flags: nextFlags };
        }, () => {
            this.changed = true;
        });
    }
    
    onCriteriaChange = (e) => {
        this.onChange({
            target: {
                name: "criteria",
                value: e
            }
        });
    }

    onBlur = (e) => {
        let { name, value, required, type, min, max, minLength, maxLength, pattern } = e.target;

        let error = false;
        value = value ?? "";

        // required
        if (required && String(value).trim() === "") {
            error = true;
        }

        minLength = Number(minLength);
        maxLength = Number(maxLength);
        

        if (!error && Number.isFinite(minLength) && minLength > 0 && value.length < minLength) error = true;
        if (!error && Number.isFinite(maxLength) && maxLength > 0 && value.length > maxLength) error = true;

        // number constraints
        if (!error && type === "number" && value !== "") {
            const n = Number(value);
            if (!Number.isFinite(n)) error = true;

            const hasMin = min !== undefined && min !== "";
            const hasMax = max !== undefined && max !== "";
            if (!error && hasMin && n < Number(min)) error = true;
            if (!error && hasMax && n > Number(max)) error = true;
        }

        // pattern (if present)
        if (!error && pattern) {
            try {
                const re = new RegExp(`^(?:${pattern})$`);
                if (!re.test(value)) error = true;
            } catch {
                // bad pattern -> ignore
            }
        }

        this.setState(prev => {
            const next = { ...prev.errors };
            if (error) next[name] = true;
            else delete next[name];
            return { errors: next };
        });
    }

    onDeleteClick = (e) => {
        if (this.isNew) {
            this.props.navigate("/dashboard/templates");
            return;
        }

        this.setState(prev => ({
            modal: {
                ...prev.modal,
                delete: {
                    title: "Delete template",
                    body: `This action will permanently delete the template named ${this.state.name}. If this template is in use, whatever function that template serves will fail to perform as expected.`,
                    show: true
                }
            }
        }));
    }
    

    onDeleteOkModal = (e) => {
        if (this.isNew) {
            this.props.navigate("/dashboard/templates");
            return;
        }

        this.props.destroy(this.state.uuid);
        this.onDeleteHideModal(e);
        this.props.navigate("/dashboard/templates");
    }

    onDeleteHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                delete: {
                    show: false
                }
            }
        }));
    }
    
    onUploadHideModal = (e) => {
        this.setState(prev => ({
            modal: {
                ...prev.modal,
                upload: {
                    show: false
                }
            }
        }));
    }
    
    onUploadOkModal = async (e, uploaded) => {
        this.onUploadHideModal(e);

        if (uploaded.file.extension === "docx") {
            const unzipped = await this.props.unzip(uploaded.file.uuid);
            const body = [];
            body.push('<root version="1">');
            unzipped.files.forEach(file => {
                let folder = file.name;
                let name = file.base;
                folder = folder.substring(0, folder.length - name.length - 1);
                const declMatch = file.data.match(/^\s*<\?xml\b[^>]*\?>/i);

                let encoding = "UTF-8";
                let standalone = "yes"; // default if not present

                if (declMatch) {
                    const decl = declMatch[0];

                    const encMatch = decl.match(/encoding\s*=\s*["']([^"']+)["']/i);
                    if (encMatch) encoding = encMatch[1];

                    const stMatch = decl.match(/standalone\s*=\s*["'](yes|no)["']/i);
                    if (stMatch) standalone = stMatch[1].toLowerCase();
                }

                body.push(
                    `<file folder="${folder}" name="${name}" transform="false" version="1.0" encoding="${encoding}" standalone="${standalone}">`
                );

                body.push(
                    file.data.replace(/^\s*<\?xml\b[^>]*\?>\s*\r?\n?/i, "")
                );
                body.push(`</file>`);
            });
            body.push('</root>');

            this.setState({
                category: CATEGORY_OPTIONS[0].value,
                name: unzipped.name,
                caption: unzipped.name,
                output: "RTF",
                template: formatXml(body.join("\r\n")),
                uuid: uuidv4()
            });
            await this.props.deleteFile(uploaded.file.uuid);
        } else if (uploaded.file.extension === "pdf") {
            const processed = await this.props.asBase64(uploaded.file.uuid);
            const { name, data } = processed;
            const fields = await this.props.pdfFields({ form: data });

            const body = [];
            body.push('<root version="1">');
            body.push(`  <file name="${name}">${data}</file>`);
            body.push(`  <mapping>`);
            body.push(`    <xsl:stylesheet version="1.0" xmlns:xsl="http://www.w3.org/1999/XSL/Transform" xmlns:xml="http://www.w3.org/XML/1998/namespace">`);
            body.push(`      <xsl:output method="xml" indent="no" encoding="UTF-8"/>`)
            body.push(`      <xsl:template match="/">`);
            body.push(`        <xsl:text>[</xsl:text>`);
            fields.forEach((element, index) => {
                body.push(`          <xsl:text>{ "name": "${element.FieldName}", "value": "</xsl:text><xsl:value-of select="''"/><xsl:text>"}${index !== fields.length - 1 ? "," : ""}</xsl:text>`);
            });
            body.push(`        <xsl:text>]</xsl:text>`);
            body.push(`      </xsl:template>`);
            body.push(`    </xsl:stylesheet>`);
            body.push(`  </mapping>`);
            body.push('</root>');
            this.setState({
                category: CATEGORY_OPTIONS[0].value,
                name: uploaded.file.name,
                caption: uploaded.file.name,
                output: "PDF",
                template: body.join("\r\n"),
                uuid: uuidv4()
            });
            await this.props.deleteFile(uploaded.file.uuid);
        }
    }

    doUploadClick = (accept) => {

        this.setState(prev => ({
            modal: {
                ...prev.modal,
                upload: {
                    ...prev.modal.upload,
                    show: true,
                    accept
                }
            }
        }))
    }

    onXmlHighlight = (e) => {
        return highlight(e, languages.xml, "xml");
    }

    onJsonHighlight = (e) => {
        return highlight(e, languages.json, "json");
    }

    onImportDocxClick = (e) => {
        this.doUploadClick("application/vnd.openxmlformats-officedocument.wordprocessingml.document")
    }

    onImportPdfClick = (e) => {
        this.doUploadClick("application/pdf");
    }

    hasFlag = (bit) => (((this.state.flags ?? 0) & bit) !== 0);

    get importItems() {
        const result = [];
        result.push({ onClick: this.onImportDocxClick, caption: "Word .docx", output: "docx", index: 0 });
        result.push({ onClick: this.onImportPdfClick, caption: "Prefilled .pdf", output: "pdf", index: 1 });
        return result;
    }

    render() {
        return (
            <>
                <Col md="8" className="float-right text-end">
                    <ConditionalDropdown className="ms-sm-1" enabledVariant="secondary" enabled display items={this.importItems}>Import From</ConditionalDropdown>
                    <ConditionalButton enabledVariant="outline-primary" tooltip={this.changed ? this.state.tooltips.pending : this.state.tooltips.test} display={true} enabled={!this.changed && !this.loading} onEnabledClick={this.onTestClick} icon="flash">Test</ConditionalButton>
                    <ConditionalButton enabledVariant="outline-primary" tooltip="Save Template" display={true} enabled={!this.loading} onEnabledClick={this.onSaveClick} icon="save">Save</ConditionalButton>
                    <ConditionalButton enabledVariant={this.isNew ? "outline-secondary" : "outline-danger"} tooltip={this.isNew ? "Cancel new template" : "Delete template"} display={true} enabled={!this.loading} onEnabledClick={this.onDeleteClick} icon={this.isNew ? "close" : "bin"}>{this.isNew ? "Cancel" : "Delete"}</ConditionalButton>
                </Col>
                
                <Col xs="12">
                    <Row>
                        <Col lg="12">
                            <Row className="g-3">
                                <Col lg="12">
                                    <h2 className="small-title">Template Information</h2>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={this.state.name || ""}
                                            onChange={this.onChange}
                                            onBlur={this.onBlur}
                                            minLength={3}
                                            required={true}
                                            isInvalid={this.state.errors?.name}
                                        />
                                        <Form.Label>NAME</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            Please enter a valid name
                                        </Form.Control.Feedback>
                                    </div>
                                </Col>
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <ValueSelect name="category" selected={this.state.category || ""} onChange={this.onChange} options={CATEGORY_OPTIONS} />
                                        <Form.Label>CATEGORY</Form.Label>
                                    </div>
                                </Col>
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="text"
                                            name="caption"
                                            value={this.state.caption || ""}
                                            onChange={this.onChange}
                                            onBlur={this.onBlur}
                                            minLength={3}
                                            required={true}
                                            isInvalid={this.state.errors?.caption}
                                        />
                                        <Form.Label>CAPTION</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            Please enter a valid caption
                                        </Form.Control.Feedback>
                                    </div>
                                </Col>
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="text"
                                            name="fileName"
                                            value={this.state.fileName || ""}
                                            onChange={this.onChange}
                                            onBlur={this.onBlur}
                                            minLength={3}
                                            required={true}
                                            isInvalid={this.state.errors?.fileName}
                                        />
                                        <Form.Label>FILE NAME TEMPLATE</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            Please enter a valid file name template
                                        </Form.Control.Feedback>
                                    </div>
                                </Col>
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <ValueSelect name="output" selected={this.state.output || ""} onChange={this.onChange} options={OUTPUT_OPTIONS} />
                                        <Form.Label>OUTPUT</Form.Label>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="g-3">
                                <Col lg="12">
                                    <h2 className="small-title">Display Circumstances</h2>
                                </Col>
                            </Row>
                            <Row className="g-3 mb-3">
                                <Col xs="12" sm="6" md="4" lg="3" xl="2">
                                    <Form.Check type="switch" id="allPeopleFlag" label="All People" checked={this.hasFlag(1)} onChange={(e) => this.onFlagChange(e.target.checked, 1)} />
                                </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                    <Form.Check type="switch" id="leadFlag" label="Leads" checked={this.hasFlag(2)} onChange={(e) => this.onFlagChange(e.target.checked, 2)} />
                                </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                    <Form.Check type="switch" id="clientFlag" label="Clients" checked={this.hasFlag(4)} onChange={(e) => this.onFlagChange(e.target.checked, 4)} />
                                </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                    <Form.Check type="switch" id="attorneyFlag" label="Attorneys" checked={this.hasFlag(8)} onChange={(e) => this.onFlagChange(e.target.checked, 8)} />
                                </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                    <Form.Check type="switch" id="judgeFlag" label="Judges" checked={this.hasFlag(16)} onChange={(e) => this.onFlagChange(e.target.checked, 16)} />
                                </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                    <Form.Check type="switch" id="staffFlag" label="Staff" checked={this.hasFlag(32)} onChange={(e) => this.onFlagChange(e.target.checked, 32)} />
                                </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                    <Form.Check type="switch" id="matterDivorceAll" label="All Matters" checked={this.hasFlag(64)} onChange={(e) => this.onFlagChange(e.target.checked, 64)} />
                                </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                    <Form.Check type="switch" id="matterDivorceFlag" label="Matters (Divorce)" checked={this.hasFlag(128)} onChange={(e) => this.onFlagChange(e.target.checked, 128)} />
                                </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                    <Form.Check type="switch" id="matterCustodyFlag" label="Matters (Custody)" checked={this.hasFlag(256)} onChange={(e) => this.onFlagChange(e.target.checked, 256)} />
                                </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                    <Form.Check type="switch" id="matterCSFlag" label="Matters (CS)" checked={this.hasFlag(512)} onChange={(e) => this.onFlagChange(e.target.checked, 512)} />
                                </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                    <Form.Check type="switch" id="matterNameChangeFlag" label="Matters (Name Change)" checked={this.hasFlag(8192)} onChange={(e) => this.onFlagChange(e.target.checked, 8192)} />
                                </Col><Col xs="12" sm="6" md="4" lg="3" xl="2">
                                    <Form.Check type="switch" id="matterCivilFlag" label="Matters (Civil)" checked={this.hasFlag(1024)} onChange={(e) => this.onFlagChange(e.target.checked, 1024)} />
                                </Col>
                            </Row>
                            
                            <CustomFieldsEditor
                                fields={this.state.customFields}
                                errors={this.state.customFieldErrors}
                                onChange={this.onCustomFieldsChange}
                            />
                            <NativeHeaderFooterEditor
                                components={this.state.components}
                                assignments={this.state.componentAssignments}
                                status={this.state.componentStatus}
                                templateSaved={!this.isNew}
                                templateDirty={this.changed}
                                compiling={this.state.compiling}
                                onAssignmentsChange={this.onComponentAssignmentsChange}
                                onSaveComponent={this.onSaveComponent}
                                onCompile={this.onCompileComponents}
                            />
                            <Row className="g-3">
                                <Col lg="12">
                                    <h2 className="small-title">Template</h2>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <CodeEditorField
                                            value={this.state.template ?? ""}
                                            onValueChange={this.onTemplateChange}
                                            highlight={this.onXmlHighlight}
                                            preClassName="language-xml"
                                            style={EDITOR_STYLE}
                                            textareaClassName="form-control"
                                            className="form-control"
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <Row className="g-3">
                                <Col lg="12">
                                    <h2 className="small-title">Criteria for Display</h2>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs="12">
                                    <div className="mb-3 top-label">
                                        <CodeEditorField
                                            value={this.state.criteria ?? ""}
                                            onValueChange={this.onCriteriaChange}
                                            highlight={this.onJsonHighlight}
                                            preClassName="language-json"
                                            textareaClassName="form-control language-json"
                                            style={EDITOR_STYLE}
                                            className="form-control"
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Col>
                <FileModal
                    show={this.state.modal.upload.show}
                    accept={this.state.modal.upload.accept}
                    title="Import Report Template"
                    uuid={this.state.uuid}
                    category="reports"
                    url={isGuid(this.props.auth?.user?.uuid) ? `files/o/person/${this.props.auth.user.uuid}/c/reports/${this.state.uuid}/upload` : ""}
                    onCancel={() => this.setState(prev => ({
                        modal: { ...prev.modal, upload: { ...prev.modal.upload, show: false } }
                    }))}
                    onOk={this.onUploadOkModal}
                >
                    Select a template to create a report from.
                </FileModal>
                <BaseModal show={this.state.modal.delete.show} title={this.state.modal.delete.title} body={this.state.modal.delete.body} buttons={
                    [
                        {
                            caption: "ok",
                            onEnabledClick: this.onDeleteOkModal,
                            variant: "primary",
                            display: true,
                            enabled: true
                        },
                        {
                            default: true,
                            caption: "cancel",
                            onEnabledClick: this.onDeleteHideModal,
                            variant: "secondary",
                            display: true,
                            enabled: true
                        }
                    ]
                }></BaseModal>
                <TestModal show={this.state.modal.test.show} customFields={this.state.customFields} onOk={this.onTestOkModal} onCancel={(e) => { this.setState(prev => ({ modal: { ...prev.modal, test: { show: false } } })) }} configurations={this.state.configurations} />
                <ScrollToTopButton />
            </>
        )
    }
}

ReportTemplateItem.propTypes = {
    auth: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired
};

const mapStateToProps = (state) => {
    return ({
        auth: state.auth
    });
};

export default withNavigation(connect(mapStateToProps,
    {
        pageInfo,
        loading,
        alert,
        getTemplate,
        getSetting,
        saveTemplate,
        destroy,
        render,
        inline,
        unzip,
        deleteFile,
        asBase64,
        getComponents,
        saveComponent,
        getComponentAssignments,
        saveComponentAssignments,
        compileTemplate,
        templateCompilation
    })(ReportTemplateItem));
